import React, { useState, useEffect, useCallback } from "react"
import {
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TablePagination,
    Paper,
    CircularProgress,
    Typography,
} from "@mui/material"
import { useTranslation } from "react-i18next"
import * as adminService from "../../services/adminService"

const thStyle = {
    fontWeight: 600,
    fontSize: "0.75rem",
    textTransform: "uppercase" as const,
    color: "#6b7280",
    letterSpacing: "0.05em",
}

interface CustomerSalesTabProps {
    companyId: string
    customerId: string
}

const CustomerSalesTab: React.FC<CustomerSalesTabProps> = ({
    companyId,
    customerId,
}) => {
    const { t, i18n } = useTranslation()
    const [sales, setSales] = useState<any[]>([])
    const [total, setTotal] = useState(0)
    const [page, setPage] = useState(0)
    const [pageSize, setPageSize] = useState(20)
    const [loading, setLoading] = useState(false)

    const loadSales = useCallback(async () => {
        if (!companyId || !customerId) return
        setLoading(true)
        try {
            const result = await adminService.getCustomerSales(
                companyId,
                customerId,
                { limit: pageSize, offset: page * pageSize }
            )
            setSales(result.sales)
            setTotal(result.total)
        } catch (error) {
            console.error("Failed to load customer sales:", error)
        } finally {
            setLoading(false)
        }
    }, [companyId, customerId, page, pageSize])

    useEffect(() => {
        loadSales()
    }, [loadSales])

    const formatAmount = (minorUnits: number) => {
        if (minorUnits == null) return t("common.dash")
        return new Intl.NumberFormat(i18n.language || "fr", {
            style: "currency",
            currency: "EUR",
        }).format(minorUnits / 100)
    }

    const formatDate = (dateStr: string) => {
        if (!dateStr) return t("common.dash")
        return new Date(dateStr).toLocaleDateString(i18n.language || "fr", {
            day: "2-digit",
            month: "2-digit",
            year: "numeric",
        })
    }

    return (
        <>
            <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell sx={thStyle}>
                                {t("customerSalesTab.columns.saleNumber")}
                            </TableCell>
                            <TableCell sx={thStyle}>
                                {t("customerSalesTab.columns.type")}
                            </TableCell>
                            <TableCell sx={thStyle} align="right">
                                {t("customerSalesTab.columns.total")}
                            </TableCell>
                            <TableCell sx={thStyle}>
                                {t("customerSalesTab.columns.date")}
                            </TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {loading ? (
                            <TableRow>
                                <TableCell
                                    colSpan={4}
                                    align="center"
                                    sx={{ py: 4 }}
                                >
                                    <CircularProgress size={24} />
                                </TableCell>
                            </TableRow>
                        ) : sales.length === 0 ? (
                            <TableRow>
                                <TableCell
                                    colSpan={4}
                                    align="center"
                                    sx={{ py: 4, color: "#9ca3af" }}
                                >
                                    <Typography variant="body2">
                                        {t("customerSalesTab.empty")}
                                    </Typography>
                                </TableCell>
                            </TableRow>
                        ) : (
                            sales.map((sale) => (
                                <TableRow key={sale.id} hover>
                                    <TableCell>
                                        {sale.sale_number || t("common.dash")}
                                    </TableCell>
                                    <TableCell>
                                        {sale.status || t("common.dash")}
                                    </TableCell>
                                    <TableCell align="right">
                                        {formatAmount(sale.gross_total_minor)}
                                    </TableCell>
                                    <TableCell>
                                        {formatDate(sale.created_at)}
                                    </TableCell>
                                </TableRow>
                            ))
                        )}
                    </TableBody>
                </Table>
            </TableContainer>
            <TablePagination
                component="div"
                count={total}
                page={page}
                onPageChange={(_, newPage) => setPage(newPage)}
                rowsPerPage={pageSize}
                onRowsPerPageChange={(e) => {
                    setPageSize(parseInt(e.target.value, 10))
                    setPage(0)
                }}
                rowsPerPageOptions={[10, 20, 50]}
                labelRowsPerPage={t("common.rowsPerPage")}
                labelDisplayedRows={({ from, to, count }) =>
                    String(
                        t("common.paginationDisplayedRows", {
                            from,
                            to,
                            count: count !== -1 ? count : to,
                        })
                    )
                }
            />
        </>
    )
}

export default CustomerSalesTab
